/* Per-box receiving model.

   A product ships as N cartons (one product.packaging.info row each). A
   scan counts one carton of one box; a unit is complete once every box of
   the product has been scanned that many times. */
import { qty } from './utils';

export const isMultiBox = (line) => (line?.boxes?.length || 0) > 1;

export const boxLabel = (box, index) => box.description || `Box ${box.seq || index + 1}`;

export function boxDims(box) {
  const parts = [];
  if (box.length > 0 && box.width > 0 && box.height > 0) parts.push(`${qty(box.length)}×${qty(box.width)}×${qty(box.height)} cm`);
  if (box.weight > 0) parts.push(`${qty(box.weight)} kg`);
  return parts.join(' · ') || null;
}

/** Complete sets for a multi-box line: the least-scanned box decides. */
export function unitsFromBoxes(line, boxCounts = {}) {
  if (!isMultiBox(line)) return null;
  return Math.min(...line.boxes.map((b) => Number(boxCounts[b.id] || 0)));
}

/** Cartons scanned beyond complete sets (a lone Box 2 without its Box 1). */
export function looseBoxes(line, boxCounts = {}, units = 0) {
  if (!isMultiBox(line)) return 0;
  return line.boxes.reduce((s, b) => s + Math.max(0, Number(boxCounts[b.id] || 0) - units), 0);
}

/** Boxes of unit number n (1-based) that have not been scanned yet. */
export function missingBoxes(line, boxCounts = {}, n) {
  return line.boxes.filter((b) => Number(boxCounts[b.id] || 0) < n);
}

/** Cartons scanned on a line, single- or multi-box. */
export function cartonsScanned(line, units = 0, boxCounts = {}) {
  if (!isMultiBox(line)) return Number(units || 0);
  return line.boxes.reduce((s, b) => s + Number(boxCounts[b.id] || 0), 0);
}

/* Find a line on the receive screen by anything printed on the carton or
   in the catalogue: product name, vendor code, product EAN, a box's vendor
   or internal EAN, or the order number. Digits also match with leading
   zeros stripped (UPC-A vs EAN-13). */
export function lineMatches(line, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [line.name, line.vendor_code, line.barcode, line.po?.name, ...(line.boxes || []).flatMap((b) => [b.vendor_barcode, b.barcode])]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());
  if (hay.some((h) => h.includes(q))) return true;
  if (!/^\d+$/.test(q)) return false;
  const digits = q.replace(/^0+/, '');
  return hay.some((h) => /^\d+$/.test(h) && h.replace(/^0+/, '') === digits);
}

/* A code just linked to a product applies to every open line of that product
   in the session, not only the line it was linked on: the next scan must be
   free to land on the other order. Returns the map with those entries added. */
export function withLinkedCode(barcodes, lines, code, productId, boxId = null) {
  const existing = barcodes[code] || [];
  const added = lines
    .filter((l) => l.product_id === productId && l.status === 'open' && !existing.some((e) => e.line_id === l.id && (e.box_id || null) === (boxId || null)))
    .map((l) => ({ line_id: l.id, kind: 'vendor', box_id: boxId || null }));
  return added.length ? { ...barcodes, [code]: [...existing, ...added] } : barcodes;
}

/* Resolve a scan that matches several lines or boxes. An EAN can be shared
   across products (the same base box on several variants) and across boxes
   of one product (a set of 4 table legs, each carton printed alike), so the
   tick goes to exactly one box of one product:
   1. a line where that box still has room,
   2. preferably one whose other boxes are ahead (the set is waiting for it),
   3. then the oldest purchase order (lowest number): it is filled completely
      before a newer order on the same truck gets any,
   4. then order-list order. */
/* What one more scan of this entry would make the line (or its box) count.
   Same rule as counting itself: a box's own tally, or the set's lowest box. */
export function nextScanCount(line, entry, progress) {
  const boxes = (progress.boxes && progress.boxes[line.id]) || {};
  const boxId = entry?.box_id || (line.boxes.length === 1 ? line.boxes[0].id : null);
  if (isMultiBox(line)) {
    if (boxId) return Number(boxes[boxId] || 0) + 1;
    return Math.min(...line.boxes.map((b) => Number(boxes[b.id] || 0))) + 1;
  }
  return Number((progress.counts || {})[line.id] || 0) + 1;
}

export function pickScanTarget(entries, lines, progress) {
  const counts = progress.counts || {};
  const candidates = entries
    .map((entry) => ({ entry, line: lines.find((l) => l.id === entry.line_id) }))
    .filter((c) => c.line && c.line.status === 'open');
  if (!candidates.length) return null;
  const scored = candidates.map(({ entry, line }) => {
    const boxes = (progress.boxes && progress.boxes[line.id]) || {};
    const multi = isMultiBox(line);
    const scanned = entry.box_id && multi ? Number(boxes[entry.box_id] || 0) : Number(counts[line.id] || 0);
    const others = multi ? Math.max(0, ...line.boxes.filter((b) => b.id !== entry.box_id).map((b) => Number(boxes[b.id] || 0))) : 0;
    return { entry, line, room: scanned < line.to_receive ? 0 : 1, waiting: multi && scanned < others ? 0 : 1, age: Number(line.po?.id || 0) };
  });
  scored.sort((a, b) => a.room - b.room || a.waiting - b.waiting || a.age - b.age);
  return { entry: scored[0].entry, line: scored[0].line, alsoOn: candidates.length - 1 };
}

/* A scanned EAN that belongs to DIFFERENT products (a shared component, the
   same base box on two items) cannot be assigned by a rule: the operator
   chooses. Returns one candidate per product, lines with room first, or null
   when there is nothing to choose (one product, possibly on several orders,
   which pickScanTarget settles with the oldest-order rule). */
export function scanChoices(entries, lines, progress) {
  const candidates = entries
    .map((entry) => ({ entry, line: lines.find((l) => l.id === entry.line_id) }))
    .filter((c) => c.line && c.line.status === 'open');
  const products = [...new Set(candidates.map((c) => c.line.product_id))];
  if (products.length < 2) return null;
  const counts = progress.counts || {};
  return products
    .map((pid) => {
      const mine = candidates.filter((c) => c.line.product_id === pid);
      const pick = pickScanTarget(mine.map((c) => c.entry), lines, progress);
      const boxes = (progress.boxes && progress.boxes[pick.line.id]) || {};
      const scanned = pick.entry.box_id && isMultiBox(pick.line) ? Number(boxes[pick.entry.box_id] || 0) : Number(counts[pick.line.id] || 0);
      return { entry: pick.entry, line: pick.line, scanned, room: scanned < pick.line.to_receive, others: mine.length - 1 };
    })
    .sort((a, b) => Number(b.room) - Number(a.room));
}
